import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

function Index() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 5;

  // Currency symbol function
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'USD': '$',
      'AFN': '؋',
      'EUR': '€'
    };
    return symbols[currencyCode] || currencyCode;
  };

  const fetchPayments = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`payments/?page=${page}&page_size=${itemsPerPage}`);
      console.log("API Response:", res.data);
      
      // Handle both paginated and non-paginated responses
      if (res.data.results !== undefined) {
        // Paginated response (Django REST Framework style)
        setPayments(res.data.results);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else if (res.data.count !== undefined) {
        // Alternative paginated response
        setPayments(res.data.data || res.data.items || []);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else {
        // Non-paginated response - manually paginate
        const allPayments = res.data;
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedPayments = allPayments.slice(startIndex, endIndex);
        
        setPayments(paginatedPayments);
        setTotalPages(Math.ceil(allPayments.length / itemsPerPage));
        setTotalCount(allPayments.length);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load payments.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) return;
    try {
      await api.delete(`payments/${id}/`);
      fetchPayments(currentPage);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete payment.");
    }
  };

  useEffect(() => {
    fetchPayments(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 text-sm border rounded-lg ${
            currentPage === i
              ? "bg-indigo-500 text-white border-indigo-500"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        →
      </button>
    );

    return buttons;
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-600 text-lg">{error}</p>
    </div>
  );

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Payments</h1>
          <p className="text-gray-600 text-sm mt-1">
            {totalCount > itemsPerPage ? `Showing ${startItem}-${endItem} of ${totalCount} payments` : `Showing ${totalCount} payment${totalCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Link
          to="/payments/create"
          className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors duration-200 flex items-center space-x-2 text-sm font-medium"
        >
          <span>+</span>
          <span>Add Payment</span>
        </Link>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Project</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Received By</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Currency</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Note</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-6 text-center">
                    <div className="text-gray-500 text-sm mb-2">No payments found</div>
                    <Link
                      to="/payments/create"
                      className="text-indigo-500 hover:text-indigo-600 text-sm font-medium"
                    >
                      Add your first payment
                    </Link>
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="p-3 text-xs font-medium text-gray-900">{p.id}</td>
                    <td className="p-3">
                      <div className="text-sm font-medium text-gray-900">
                        {p.project?.title || p.project || "-"}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-600">
                        {p.paid_by_name || "-"}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm font-medium text-gray-700">
                        {p.currency || "AFN"}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm font-semibold text-green-600">
                        {getCurrencySymbol(p.currency || 'AFN')}{parseFloat(p.amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-600 max-w-[150px] truncate">
                        {p.note || "-"}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-500">
                        {new Date(p.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/payments/edit/${p.id}`}
                          className="text-blue-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition-colors duration-200"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                        </Link>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors duration-200"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards - Compact */}
      <div className="md:hidden space-y-3">
        {payments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-gray-500 text-sm mb-2">No payments found</div>
            <Link
              to="/payments/create"
              className="text-indigo-500 hover:text-indigo-600 text-sm font-medium"
            >
              Add your first payment
            </Link>
          </div>
        ) : (
          payments.map((p) => (
            <div key={p.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs text-gray-500">ID: {p.id}</span>
                    <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                      {p.currency || "AFN"}
                    </span>
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    {getCurrencySymbol(p.currency || 'AFN')}{parseFloat(p.amount).toFixed(2)}
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <Link
                    to={`/payments/edit/${p.id}`}
                    className="text-blue-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition-colors duration-200"
                    title="Edit"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </Link>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors duration-200"
                    title="Delete"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                  <span className="text-xs text-gray-600 truncate">
                    {p.project?.title || p.project || "No project"}
                  </span>
                </div>
                
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  <span className="text-xs text-gray-600 truncate">
                    {p.paid_by_name || "-"}
                  </span>
                </div>
                
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <span className="text-xs text-gray-600">
                    {new Date(p.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                {p.note && (
                  <div className="flex items-start">
                    <svg className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    <span className="text-xs text-gray-600 flex-1">{p.note}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Showing {startItem}-{endItem} of {totalCount} payments
          </div>
          <div className="flex items-center space-x-1 flex-wrap justify-center">
            {renderPaginationButtons()}
          </div>
        </div>
      )}
    </div>
  );
}

export default Index;